import { queryOptions } from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import { getItemDerivedStatus } from '@/lib/bulk-jobs/status'
import type { BulkJobItemDerivedStatus } from '@/lib/bulk-jobs/types'
import { supabase } from '@/lib/supabase/client'

// Re-export shared type for backwards compatibility
export type { BulkJobItemDerivedStatus as VtoBulkJobItemDerivedStatus }

// =============================================================================
// VTO Bulk Job Item Types
// =============================================================================

type VtoBulkJobItemRow =
  Database['public']['Tables']['vto_bulk_job_items']['Row']

/**
 * VTO Bulk job item with VTO look and generation details
 * Status is derived from vto_look.current_generation.status
 */
export type VtoBulkJobItem = VtoBulkJobItemRow & {
  outfits: {
    id: string
    name: string
  } | null
  vto_looks: {
    id: string
    name: string
    current_generation_id: string | null
    generations: {
      id: string
      status: string
      storage_path: string | null
    } | null
  } | null
}

/**
 * Derive the status of a bulk job item from its vto_look/generation.
 * Delegates to shared logic with VTO-specific field mapping.
 */
export function getVtoBulkJobItemStatus(
  item: VtoBulkJobItem
): BulkJobItemDerivedStatus {
  return getItemDerivedStatus({
    error_message: item.error_message,
    result_entity_id: item.vto_look_id,
    generation_status: item.vto_looks?.generations?.status ?? null,
  })
}

// =============================================================================
// VTO Bulk Job Item Queries
// =============================================================================

export const vtoBulkJobItem = {
  findManyByJob: (jobId: string) =>
    queryOptions({
      queryKey: ['vto-bulk-job-items', 'job', jobId],
      queryFn: async (): Promise<VtoBulkJobItem[]> => {
        if (!jobId || jobId.trim() === '') {
          throw new Error(
            'Invalid jobId: cannot fetch VTO bulk job items without valid job ID'
          )
        }

        const { data, error } = await supabase
          .from('vto_bulk_job_items')
          .select(
            `
            *,
            outfits!outfit_id(
              id,
              name
            ),
            vto_looks!vto_look_id(
              id,
              name,
              current_generation_id,
              generations!current_generation_id(
                id,
                status,
                storage_path
              )
            )
          `
          )
          .eq('bulk_job_id', jobId)
          .order('created_at', { ascending: true })

        if (error) {
          throw new Error(
            `Failed to fetch VTO bulk job items: ${error.message}`,
            { cause: error }
          )
        }

        return (data || []) as VtoBulkJobItem[]
      },
      staleTime: 10 * 1000,
    }),

  findCompletedByJob: (jobId: string) =>
    queryOptions({
      queryKey: ['vto-bulk-job-items', 'job', jobId, 'completed'],
      queryFn: async (): Promise<VtoBulkJobItem[]> => {
        if (!jobId || jobId.trim() === '') {
          throw new Error(
            'Invalid jobId: cannot fetch VTO bulk job items without valid job ID'
          )
        }

        const { data, error } = await supabase
          .from('vto_bulk_job_items')
          .select(
            `
            *,
            outfits!outfit_id(
              id,
              name
            ),
            vto_looks!vto_look_id(
              id,
              name,
              current_generation_id,
              generations!current_generation_id(
                id,
                status,
                storage_path
              )
            )
          `
          )
          .eq('bulk_job_id', jobId)
          .not('vto_look_id', 'is', null)
          .order('created_at', { ascending: true })

        if (error) {
          throw new Error(
            `Failed to fetch completed VTO bulk job items: ${error.message}`,
            { cause: error }
          )
        }

        // Filter to only completed generations
        const completedItems = (data || []).filter((item) => {
          const typedItem = item as VtoBulkJobItem
          return typedItem.vto_looks?.generations?.status === 'completed'
        })

        return completedItems as VtoBulkJobItem[]
      },
      staleTime: 30 * 1000,
    }),
}
